import React, { useEffect, useRef, useState } from 'react';
import './publicPaymentComplete.css';

// PUBLIC (no-auth) payment completion page — /payment-complete?ref=<order_ref>.
// Mobile in-app browsers land here after a hosted-paypage checkout started in
// the app: there is no web session, so the page polls the unauthenticated
// GET /api/payments/public-status?ref= and shows ONLY the outcome, the kind
// label and the amount — no client data of any kind.
//
// Uses fetch (not axios) on purpose: the app's global axios 401 interceptor
// logs out + hard-redirects to /login, which must never fire on a public page.
//
// The outcome states are PERSISTENT — no auto-dismiss, no timers hiding the
// result. The client closes the browser / returns to the app themselves.

const POLL_MS = 3000;
const MAX_POLL_MS = 120000; // 2 minutes, then a manual "Check again"

const money = (n) =>
    `AED ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicPaymentComplete() {
    const [orderRef] = useState(() => new URLSearchParams(window.location.search).get('ref'));
    // phase: 'checking' | 'paid' | 'verified' | 'failed' | 'expired' | 'pending_timeout' | 'no_ref' | 'error'
    //   ('verified' = card-verification success on the AUTH+void rail — the
    //    card was saved, NOTHING was captured. Rendered from the payload's own
    //    verification.message and never as a payment: no amount, no receipt.)
    const [phase, setPhase] = useState(orderRef ? 'checking' : 'no_ref');
    const [info, setInfo] = useState(null); // { outcome, kind_label, amount }
    const [pollSeq, setPollSeq] = useState(0); // bump to restart polling ("Check again")
    const timerRef = useRef(null);

    useEffect(() => {
        if (!orderRef) return undefined;
        let cancelled = false;
        const startedAt = Date.now();
        setPhase('checking');

        const poll = async () => {
            try {
                const res = await fetch(`/api/payments/public-status?ref=${encodeURIComponent(orderRef)}`, {
                    headers: { Accept: 'application/json' },
                });
                if (cancelled) return;
                if (res.status === 404) { setPhase('error'); return; }
                if (!res.ok) throw new Error(`status ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                setInfo(data);
                const outcome = String(data?.outcome || '').toLowerCase();
                if (outcome === 'paid') { setPhase('paid'); return; }
                // Card-verification success ('card_verified' = backend alias).
                // Terminal — left unmapped this kept polling into the timeout.
                if (outcome === 'verified' || outcome === 'card_verified') { setPhase('verified'); return; }
                if (outcome === 'failed' || outcome === 'cancelled') { setPhase('failed'); return; }
                if (outcome === 'expired') { setPhase('expired'); return; }
                // pending → keep polling within the window
                if (Date.now() - startedAt < MAX_POLL_MS) timerRef.current = setTimeout(poll, POLL_MS);
                else setPhase('pending_timeout');
            } catch {
                if (cancelled) return;
                if (Date.now() - startedAt < MAX_POLL_MS) timerRef.current = setTimeout(poll, POLL_MS);
                else setPhase('pending_timeout');
            }
        };
        poll();

        return () => {
            cancelled = true;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [orderRef, pollSeq]);

    const kindLabel = info?.kind_label || 'Payment';
    const amount = info?.amount;

    let tone; let icon; let title; let body;
    switch (phase) {
        case 'paid':
            tone = 'paid';
            icon = '✓';
            title = 'Payment successful';
            body = 'Your payment has been confirmed. You can now return to the app — everything will be up to date there.';
            break;
        case 'verified':
            // Card saved off a temporary AUTH hold — NOT a payment. The body is
            // the backend's own truthful hold line (verification.message: "you
            // have not been charged" only rides on a verifiably released void,
            // otherwise it describes a standing hold that lapses). The fallback
            // makes no claim about the hold either way. No amount, no kind row
            // below — a figure here would read as a receipt for a charge that
            // never happened.
            tone = 'paid';
            icon = '✓';
            title = 'Card verified and saved';
            body = info?.verification?.message || 'Your card has been verified and saved securely.';
            break;
        case 'failed':
            tone = 'failed';
            icon = '✕';
            title = 'Payment was not completed';
            body = 'Nothing was charged. You can now return to the app to try the payment again or choose another way to pay.';
            break;
        case 'expired':
            tone = 'amber';
            icon = '⏱';
            title = 'Payment session expired';
            body = 'The secure payment page timed out and nothing was charged. You can now return to the app to try again.';
            break;
        case 'pending_timeout':
            tone = 'amber';
            icon = '⏱';
            title = 'Still confirming your payment';
            body = 'The bank has not given a final answer yet. You can return to the app — your account updates automatically the moment the payment is confirmed.';
            break;
        case 'no_ref':
        case 'error':
            tone = 'neutral';
            icon = '?';
            title = 'Payment not found';
            body = 'We couldn’t find this payment. Please return to the app to check your account.';
            break;
        case 'checking':
        default:
            tone = 'checking';
            icon = null;
            title = 'Confirming your payment…';
            body = 'We’re checking with the bank — this usually takes a few seconds. Please keep this page open.';
            break;
    }

    return (
        <div className="ppc">
            <div className="ppc__brand">
                <img src="/logo.png" alt="Holiday Home Cleaning" className="ppc__logo" />
            </div>
            <div className={`ppc__card ppc__card--${tone}`}>
                <div className={`ppc__icon ppc__icon--${tone}`}>
                    {phase === 'checking' ? <span className="ppc__spinner" aria-hidden="true" /> : icon}
                </div>
                <h1 className="ppc__title">{title}</h1>
                {(phase === 'paid' || phase === 'failed' || phase === 'expired') && amount != null && (
                    <div className="ppc__amount">{money(amount)}</div>
                )}
                {(phase === 'paid' || phase === 'failed' || phase === 'expired') && (
                    <div className="ppc__kind">{kindLabel}</div>
                )}
                <p className="ppc__body">{body}</p>
                {phase === 'pending_timeout' && (
                    <button
                        type="button"
                        className="ppc__btn"
                        onClick={() => setPollSeq((s) => s + 1)}
                    >
                        Check again
                    </button>
                )}
                {phase !== 'checking' && (
                    <div className="ppc__returnNote">You can now return to the app.</div>
                )}
            </div>
            <div className="ppc__secure">Secure payment · verified with the bank</div>
        </div>
    );
}
